import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import * as React from "react";
import { replaceHashWithValue } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { MuiBox } from "../box/MuiBox";
import { MuiIconButton } from "../button/MuiButton";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiInfoToolTip } from "../tooltip/infoToolTip/MuiInfoToolTip";
import { ChipWrapper } from "./MuiChip.style";

export interface ChipOptionsDataType {
  value: string | number;
  label: string;
  additionalInfo?: string;
}

export interface MuiChipProps {
  data: ChipOptionsDataType[];
  isExpanded?: boolean;
  handleExpand?: (event: React.MouseEvent<HTMLElement>) => void;
  handleDelete?: (chipValue: number | string, openFlag: boolean) => void;
  id?: string;
  isEditable?: boolean;
  infoMessage?: string;
  countToWrap?: number;
  wrapperClass?: string;
  showMoreARIALabel: string;
  removeChipNameARIALabel: string;
}

const MuiChip: React.FC<MuiChipProps> = ({
  data,
  isExpanded,
  handleExpand,
  handleDelete,
  id = "",
  isEditable = true,
  countToWrap = 2,
  infoMessage,
  wrapperClass = "",
  showMoreARIALabel,
  removeChipNameARIALabel,
}) => {
  const { commonLabels } = useSiteCore();
  return (
    <ChipWrapper className={wrapperClass}>
      <Stack
        direction="row"
        spacing={2}
        className={isExpanded ? "popover-stack" : ""}
      >
        {data && !isExpanded && (
          <>
            {/* display only first countToWrap */}
            {data.slice(0, countToWrap).map((item, index) => (
              <Chip
                data-testid={`chip-${item.value}`}
                role="none"
                key={item.value}
                label={
                  <>
                    <MuiEllipseLabel
                      text={item.label}
                      labelTypoClassName="label"
                    />
                    {infoMessage && (
                      <MuiBox>
                        <MuiInfoToolTip
                          message={infoMessage}
                          placement="top"
                          shouldDisableInfoIcon={false}
                        />
                      </MuiBox>
                    )}
                  </>
                }
                onDelete={() => handleDelete?.(item.value, false)}
                deleteIcon={
                  isEditable ? (
                    <MuiIconButton
                      aria-label={replaceHashWithValue(
                        removeChipNameARIALabel,
                        item.label,
                      )}
                    >
                      <CloseRoundedIcon className="close-image" />
                    </MuiIconButton>
                  ) : (
                    <></>
                  )
                }
                className={!isEditable ? "view-only-chip" : ""}
                id={`${id}-${index}`}
              />
            ))}
            {data.length > countToWrap && (
              <Chip
                data-testid={`chip-more`}
                key={data.length}
                label={`+${data.length - countToWrap} ${commonLabels.more}`}
                className="more-text"
                onClick={handleExpand}
                aria-label={replaceHashWithValue(
                  showMoreARIALabel,
                  `${data.length - countToWrap}`,
                )}
              />
            )}
          </>
        )}
        {data && isExpanded && (
          <>
            {data.map((item, index) => (
              <Chip
                data-testid={`chip-${item.value}`}
                role="none"
                className={!isEditable ? "mui-chip view-only-chip" : "mui-chip"}
                key={item.value}
                label={
                  <>
                    <MuiEllipseLabel
                      text={item.label}
                      labelTypoClassName="label"
                    />
                  </>
                }
                onDelete={() =>
                  handleDelete ? handleDelete(item.value, true) : {}
                }
                deleteIcon={
                  isEditable ? (
                    <MuiIconButton
                      aria-label={replaceHashWithValue(
                        removeChipNameARIALabel,
                        item.label,
                      )}
                    >
                      <CloseRoundedIcon className="close-image" />
                    </MuiIconButton>
                  ) : (
                    <></>
                  )
                }
                id={`${id}-modal-${index}`}
              />
            ))}
          </>
        )}
      </Stack>
    </ChipWrapper>
  );
};

export default MuiChip;
